import 'react-day-picker/style.css';

import clsx from 'clsx';
import { startOfDay } from 'date-fns';
import { useId } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';

import CalendarIcon from '@/assets/icons/calendar.svg';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { formatShortDate } from '@/utils/date-util';

import { Calendar } from './calendar';
import { Skeleton } from './skeleton';
import { Typography } from './typography';

interface DatePickerProps {
  id?: string;
  date?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isFetching?: boolean;
}

export function DatePicker({
  id,
  date,
  onChange,
  placeholder,
  isDisabled,
  isFetching,
}: DatePickerProps) {
  const renderDateContent = () => {
    if (isFetching) return <Skeleton className="h-4 w-2/3" />;

    if (!date) return <span>{placeholder}</span>;

    return formatShortDate(date);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          disabled={isDisabled}
          className={clsx(
            'w-full justify-start rounded-sm pl-2 font-normal',
            !date && 'text-fx-neutral-400',
          )}
        >
          <img src={CalendarIcon} alt="Calendar" />
          {renderDateContent()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="pointer-events-auto w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={onChange}
          autoFocus
          disabled={{ before: new Date() }}
          defaultMonth={date}
        />
      </PopoverContent>
    </Popover>
  );
}

interface FormDatePickerProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isFetching?: boolean;
  isStartOfDay?: boolean;
  isEndOfDay?: boolean;
}

// A wrapper over the above DatePicker component to integrate with react-hook-form
export function FormDatePicker<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  isDisabled,
  isFetching,
  isStartOfDay,
  isEndOfDay,
}: FormDatePickerProps<TFieldValues>) {
  const id = useId();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className="flex w-full flex-col gap-1">
          {label && (
            <label htmlFor={id}>
              <Typography variant="subtitle2">{label}</Typography>
            </label>
          )}
          <DatePicker
            date={field.value}
            onChange={date => {
              if (!date) return;
              if (isStartOfDay) field.onChange(startOfDay(date).toISOString());
              else if (isEndOfDay)
                field.onChange(startOfDay(date).toISOString());
              else field.onChange(date.toISOString());
            }}
            id={id}
            placeholder={placeholder}
            isDisabled={isDisabled}
            isFetching={isFetching}
          />
        </div>
      )}
    />
  );
}
